import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import { Button, Heading, GhostLink } from '@movember/mo-gel';
import { StyledInputGroup, StyledQuestionContainer, StyledPreviousQuestion, StyledSeekSkip } from './InputGroup.styled';
import * as InputsService from '../../services/inputsService';
import * as OutcomeSearchService from '../../services/outcomesSearch';
import { QuestionAnswer } from '../../components/QuestionAnswer';

import { areEqual } from '../../utils/equalityChecker';

import {
  pushRouteChange,
  pushFormFieldComplete,
  pushFormFieldView,
  pushFormComplete,
  nameMapper
} from '../../utils/gtmEventPush';

class InputGroupContainer extends Component {
  componentName = 'InputGroup.container';

  constructor(props) {
    super(props);

    const { selectedInputGroup, selectedSubInputGroup, routeInfo, skipInputLoad } = props;

    this.state = {
      currentStep: 0,
      // the subInput object per primary question
      subInputs: {},
      // the collection of selected answers for the primary question
      currentInputGroup: selectedInputGroup,
      // the collection of selected subInputs per primary question
      selectedSubInputs: selectedSubInputGroup,
      displaySubInputs: false,
      parsedInputs: undefined,

      // Google Tag Manager Data Layer
      selectedValues: '', // eslint-disable-line react/no-unused-state
      prevTime: undefined,
      previousRoute: routeInfo.previousRoute,
      currentRoute: routeInfo.currentRoute,

      // flag to clear input history
      skipInputLoad
    };
  }

  componentDidMount() {
    const { parsedInputs, currentInputGroup, skipInputLoad } = this.state;
    const { inputs, onSkipInputLoad } = this.props;
    const currentTime = new Date().getTime() / 1000;

    if (!parsedInputs) {
      const newParsedInputs = InputsService.inputsDataParser(inputs, currentInputGroup);
      this.setState({
        parsedInputs: newParsedInputs,
        prevTime: currentTime
      });
    }
    if (skipInputLoad) {
      onSkipInputLoad();
    } else {
      this.setStateFromHistory();
    }
    window.addEventListener('popstate', this.onBackOrForwardButtonEvent);
  }

  componentDidUpdate() {
    if (!window.history.state || !areEqual(window.history.state[this.componentName], this.state)) {
      const newState = {};
      Object.assign(newState, window.history.state);
      newState[this.componentName] = this.state;
      if (window.history.state && Object.prototype.hasOwnProperty.call(window.history.state, this.componentName)) {
        window.history.pushState(newState, '');
      } else {
        window.history.replaceState(newState, '');
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener('popstate', this.onBackOrForwardButtonEvent);
  }

  onBackOrForwardButtonEvent = () => {
    this.setStateFromHistory();
  };

  setStateFromHistory = () => {
    if (window.history.state && Object.prototype.hasOwnProperty.call(window.history.state, this.componentName)) {
      if (!areEqual(window.history.state[this.componentName], this.state)) {
        this.setState(window.history.state[this.componentName]);
      }
    }
  };

  getTimeDiff = () => {
    const { prevTime } = this.state;
    return Math.round(new Date().getTime() / 1000 - prevTime);
  };

  onSubInputItemSelect = (input, value, route, isRadio) => {
    const { subInputs, selectedSubInputs, parsedInputs } = this.state;
    // find the selected value and assign it to subInputs state
    const selectedInput = subInputs.options.find((subInput) => subInput.itemKey === value);
    subInputs.selectedValue = selectedInput;

    // update selected sub inputs state to remember which one is selected for which primary question
    const selectedMainInput = parsedInputs[parsedInputs.indexOf(input)];
    const selectedSubInputGroup = {
      name: selectedMainInput.name,
      id: selectedMainInput.selectedValue.itemKey,
      selectedValue: selectedInput
    };

    const selectedInputExists = selectedSubInputs.find((subInput) => subInput.name === selectedMainInput.name);

    if (isRadio) {
      setTimeout(() => {
        this.updateSubInputState(input, route, selectedSubInputGroup, selectedInputExists);
      }, 800);
    } else {
      this.updateSubInputState(input, route, selectedSubInputGroup, selectedInputExists);
    }
  };

  updateSubInputState = (input, route, selectedSubInputGroup, selectedInputExists) => {
    const { subInputs, selectedSubInputs } = this.state;

    if (!selectedInputExists) {
      selectedSubInputs.push(selectedSubInputGroup);
    } else {
      selectedSubInputs[selectedSubInputs.indexOf(selectedInputExists)] = selectedSubInputGroup;
    }

    this.setState({
      displaySubInputs: false,
      selectedSubInputs,
      subInputs
    });
    this.nextQuestion(null, input.name, route);
  };

  updateSubInput = (selectedInputOptions, inputName, route, routeChangeInfo) => {
    const { selectedSubInputs } = this.state;
    const { subOptions } = selectedInputOptions || [];

    const hasSubOptions = subOptions && subOptions.length > 0;
    const selectedSubInputValue =
      selectedInputOptions &&
      selectedSubInputs &&
      selectedSubInputs.find((selectedSubInput) => selectedSubInput.id === selectedInputOptions.itemKey);

    let removedSelectedSubInput = selectedSubInputs;
    let optionSubInputs;

    if (hasSubOptions) {
      optionSubInputs = {
        id: selectedInputOptions.itemKey,
        question: selectedInputOptions.subOptionsQuestion,
        options: subOptions,
        selectedValue: selectedSubInputValue ? selectedSubInputValue.selectedValue : null
      };
    } else {
      removedSelectedSubInput = selectedSubInputs.filter((value) => value.name !== inputName);
    }

    this.setState({
      subInputs: optionSubInputs,
      selectedSubInputs: removedSelectedSubInput
    });
    this.nextQuestion(hasSubOptions, inputName, route, routeChangeInfo);
  };

  onItemSelect = (inputObject, value, route, isRadioInput) => {
    const { parsedInputs } = this.state;

    if (inputObject) {
      // update selected value in parsed inputs
      let selectedInputOptions;
      if (inputObject.name === 'ltc_group') {
        selectedInputOptions = {
          itemKey: value,
          text:
            value > 0 || value.length > 0 ? `You have selected ${value.length} options` : `No other health conditions`
        };
      } else {
        selectedInputOptions = inputObject.options.find((option) => option.itemKey === value);
      }
      parsedInputs[parsedInputs.indexOf(inputObject)].selectedValue = selectedInputOptions;

      if (isRadioInput) {
        // wait for selection animation to complete before transitioning to next question
        setTimeout(() => {
          this.updateInputsGroups(inputObject, selectedInputOptions, route);
        }, 800);
      } else {
        this.updateInputsGroups(inputObject, selectedInputOptions, route);
      }
    }
  };

  updateInputsGroups = (inputObject, selectedInputOptions, route) => {
    this.updateSelectedValueInParsedInputs(inputObject.name, selectedInputOptions);
    this.updateCurrentInputGroup(inputObject.name, selectedInputOptions);
    this.updateSubInput(selectedInputOptions, inputObject.name, route);
  };

  updateSelectedValueInParsedInputs = (inputName, selectedValue) => {
    const { parsedInputs } = this.state;
    parsedInputs[parsedInputs.findIndex((input) => input.name === inputName)].selectedValue = selectedValue;
    this.setState({
      parsedInputs
    });
  };

  updateCurrentInputGroup = (inputName, selectedValue) => {
    const { currentInputGroup } = this.state;
    const selectedAnswer = {
      name: inputName,
      selectedValue
    };
    const inputAnswerExists = currentInputGroup.find((currentInput) => currentInput.name === inputName);

    if (inputAnswerExists) {
      currentInputGroup[currentInputGroup.indexOf(inputAnswerExists)] = selectedAnswer;
    } else {
      currentInputGroup.push(selectedAnswer);
    }

    this.setState({ currentInputGroup });
  };

  onSkipInput = (input) => {
    const { parsedInputs, currentInputGroup, currentStep, processId } = this.state;
    const skippedSelectedValue = { itemKey: null, text: 'Question Skipped' };

    // update selected value in parsed inputs
    parsedInputs[parsedInputs.findIndex((i) => i.name === input.name)].selectedValue = skippedSelectedValue;

    // update current input group
    const currentInputGroupIndex = currentInputGroup.findIndex((i) => i.name === input.name);
    if (currentInputGroupIndex > 0) {
      currentInputGroup[currentInputGroupIndex].selectedValue = skippedSelectedValue;
    } else {
      currentInputGroup.push({ name: input.name, selectedValue: skippedSelectedValue });
    }

    const currentTime = new Date();

    pushFormFieldComplete(input, currentStep, this.getTimeDiff(), 'Question skipped', processId);

    this.setState({
      currentInputGroup,
      parsedInputs,
      prevTime: currentTime.getTime() / 1000,
      previousRoute: input.name
    });
    this.nextQuestion(null, input);
  };

  onNextQuestionClick = (input) => {
    const { currentStep, processId } = this.state;
    pushFormFieldComplete(input, currentStep, this.getTimeDiff(), 'Next Question Clicked', processId);

    const hasSubInputs =
      input.selectedValue && input.selectedValue.subOptions && input.selectedValue.subOptions.length > 0;
    if (hasSubInputs) {
      this.updateSubInput(input.selectedValue, input.name);
    }
    this.nextQuestion(hasSubInputs, input, input.name);
  };

  handleItemSelect = (event, inputObject, value, route) => {
    const { displaySubInputs, currentStep, processId, previousRoute } = this.state;

    const currentTime = new Date();

    const input = inputObject;
    if (previousRoute === 'treatment' || nameMapper[previousRoute] === 'treatment') {
      input.label = 'Secondary treatment';
    }

    let innerContent = event.target.innerText;
    if (!innerContent && event.target.parentNode) {
      innerContent = event.target.parentNode.innerText;
    }

    pushFormFieldComplete(
      inputObject,
      currentStep,
      this.getTimeDiff(),
      nameMapper[inputObject.name] === 'other conditions' ? event.target.children[0].textContent : innerContent,
      processId
    );

    this.setState({
      prevTime: currentTime.getTime() / 1000
    });

    const isRadio = event.target.type === 'radio';

    if (displaySubInputs) {
      this.onSubInputItemSelect(inputObject, value, route, isRadio);
    } else {
      this.onItemSelect(inputObject, value, route, isRadio);
    }
  };

  onPreviousInput = () => {
    const { currentStep, displaySubInputs, selectedSubInputs, parsedInputs } = this.state;

    this.setState(
      {
        currentStep: currentStep > 0 ? currentStep - 1 : currentStep
      },
      () => {
        const { currentStep } = this.state; // eslint-disable-line no-shadow

        if (displaySubInputs) {
          const { previousRoute, currentRoute } = this.state;
          const current = currentStep === 0 ? nameMapper[parsedInputs[currentStep].name] : previousRoute;
          const previous = currentRoute;

          pushRouteChange(current, previous);
          this.setState({
            displaySubInputs: false,
            previousRoute: `${previous}`,
            currentRoute: `${current}`
          });
        } else {
          const currentInput = parsedInputs[currentStep];
          const current = currentStep === 0 ? 'secondaryTreatment' : currentInput.name;
          const previous = (parsedInputs[currentStep + 1] && parsedInputs[currentStep + 1].name) || 'loadresults';

          this.setState(
            {
              previousRoute: `${previous}`,
              currentRoute: `${current}`
            },
            () => {
              const { previousRoute, currentRoute } = this.state;
              pushRouteChange(currentRoute, previousRoute);

              const selectedSubOption = selectedSubInputs
                ? selectedSubInputs.find((subInput) => subInput.name === currentInput.name)
                : null;
              if (selectedSubOption) {
                const selectedInputGroup = currentInput.options.find(
                  (option) => option.itemKey === selectedSubOption.id
                );
                this.updateSubInput(selectedInputGroup, currentInput.name, null, { current, previous });
              }
            }
          );
        }
      }
    );
  };

  onInputCompleted = () => {
    const { onInputCompleted, inputs, formStartTime, processId } = this.props;
    const { currentInputGroup, selectedSubInputs, currentRoute } = this.state;

    if (onInputCompleted) {
      const timeSince = Math.round((new Date().getTime() - formStartTime) / 1000);

      pushFormComplete(timeSince, this.getSelectedValues(), processId);

      pushRouteChange('results', currentRoute);

      const outcomeIndex = OutcomeSearchService.mapInputGroups(currentInputGroup, selectedSubInputs, inputs);
      onInputCompleted(currentInputGroup, outcomeIndex, selectedSubInputs);
    }
  };

  getSelectedValues = () => {
    const { currentInputGroup, selectedSubInputs } = this.state;
    let selectedValues = '';

    currentInputGroup.forEach((ig) => {
      selectedValues = selectedValues === '' ? ig.selectedValue.text : `${selectedValues} | ${ig.selectedValue.text}`;
      if (selectedSubInputs.length > 0) {
        selectedSubInputs.forEach((subInput) => {
          if (subInput.name === ig.name) {
            selectedValues = `${selectedValues} | ${subInput.selectedValue.text}`;
          }
        });
      }
    });

    return selectedValues;
  };

  renderPreviousSelection = () => {
    const { currentStep, displaySubInputs, parsedInputs } = this.state;

    if (currentStep === 0 && !displaySubInputs) {
      return null;
    }

    return (
      <GhostLink
        onClick={this.onPreviousInput}
        icon="up"
        data-track-type="Previous Selection"
        data-track-location={nameMapper[parsedInputs[currentStep] && parsedInputs[currentStep].name] || 'Load Results'}
      >
        Previous
      </GhostLink>
    );
  };

  nextQuestion = (hasSubInputs, input, route, routeChangeInfo = null) => {
    const { currentStep, displaySubInputs, parsedInputs, currentRoute } = this.state;
    const { processId } = this.props;
    const step = hasSubInputs && !displaySubInputs ? currentStep : currentStep + 1;

    pushFormFieldView(
      input,
      step,
      parsedInputs && parsedInputs[step] && parsedInputs[step].name,
      this.getTimeDiff(),
      processId
    );

    let currentPage;
    let previousPage;

    if (step === 0 && !displaySubInputs) {
      currentPage = 'secondaryTreatment';
      previousPage = currentRoute;
    } else if (step === 1) {
      previousPage = currentRoute;
    } else {
      previousPage = parsedInputs && parsedInputs[step - 1] && parsedInputs[step - 1].name;
    }

    const current =
      step === 0 ? currentPage : (parsedInputs && parsedInputs[step] && parsedInputs[step].name) || 'loadResults';
    const previous = previousPage || 'undefined';

    if (routeChangeInfo !== null) pushRouteChange(routeChangeInfo.current, routeChangeInfo.previous);
    else pushRouteChange(current, previous);

    this.setState({
      currentStep: step,
      displaySubInputs: hasSubInputs && !displaySubInputs,
      previousRoute: `${previous}`,
      currentRoute: `${current}`
    });
  };

  render() {
    const { theme, fetching } = this.props;
    const { currentStep, subInputs, displaySubInputs, parsedInputs } = this.state;
    if (!parsedInputs) {
      return null;
    }
    return (
      <StyledInputGroup>
        <StyledPreviousQuestion>{this.renderPreviousSelection()}</StyledPreviousQuestion>

        {parsedInputs.map((input, index) => {
          if (currentStep !== index) return null;
          return (
            <div key={`${displaySubInputs ? 'secondary' : 'primary'}-input-${index}`}>
              <StyledQuestionContainer>
                <QuestionAnswer
                  name={input.name}
                  theme={theme}
                  index={index}
                  input={displaySubInputs ? subInputs : input}
                  onItemSelect={(event, value) => {
                    this.handleItemSelect(event, input, value, input.name);
                  }}
                />
              </StyledQuestionContainer>
              <StyledSeekSkip>
                {((displaySubInputs && subInputs.selectedValue && subInputs.selectedValue.itemKey) ||
                  (!displaySubInputs && input.selectedValue && input.selectedValue.itemKey != null)) && (
                  <Button
                    primary
                    onClick={() => {
                      this.onNextQuestionClick(input);
                    }}
                    data-track-type="Next Question"
                    data-track-location={nameMapper[input.name]}
                  >
                    {currentStep === parsedInputs.length - 1 ? 'Submit' : 'Next Question'}
                  </Button>
                )}
                {!input.isRequired && (
                  <Button
                    tertriary
                    textColor={theme.colors.white}
                    margin="5px 0"
                    onClick={() => this.onSkipInput(input)}
                    data-track-type="Skip"
                    data-track-location={nameMapper[input.name]}
                  >
                    Skip Question
                  </Button>
                )}
              </StyledSeekSkip>
            </div>
          );
        })}

        {currentStep === parsedInputs.length && (
          <StyledQuestionContainer>
            <Heading level="h4" margin="0" color={theme.colors.white}>
              Great, you&apos;re all done here.
            </Heading>
            <Button
              primary
              onClick={this.onInputCompleted}
              disabled={fetching}
              data-track-type="Load Results"
              data-track-location={fetching ? 'Fetching data...' : 'Load results'}
            >
              {fetching ? 'Fetching data...' : 'Load results'}
            </Button>
          </StyledQuestionContainer>
        )}
      </StyledInputGroup>
    );
  }
}

const InputGroupContainerWrapper = withTheme(InputGroupContainer);
export { InputGroupContainerWrapper as InputGroupContainer };

InputGroupContainer.defaultProps = {
  inputs: [{}],
  onInputCompleted: () => {},
  theme: {},
  fetching: false,
  selectedInputGroup: [],
  selectedSubInputGroup: [],
  processId: '',
  formStartTime: new Date().getTime() / 1000,
  selectedValues: '',
  routeInfo: {
    currentRoute: 'trt',
    previousRoute: '',
    title: 'trt'
  }
};

InputGroupContainer.propTypes = {
  inputs: PropTypes.arrayOf(PropTypes.object),
  onInputCompleted: PropTypes.func,
  theme: PropTypes.shape({ theme: PropTypes.object }),
  fetching: PropTypes.bool,
  selectedInputGroup: PropTypes.arrayOf(PropTypes.object),
  selectedSubInputGroup: PropTypes.arrayOf(PropTypes.object),
  processId: PropTypes.string,
  formStartTime: PropTypes.number,
  selectedValues: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
  routeInfo: PropTypes.shape({
    currentRoute: PropTypes.string,
    previousRoute: PropTypes.string,
    title: PropTypes.string
  })
};
